import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiClient } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import { useState } from 'react'

type Item = {
  appliedStatus: string | null
  proposalId: string
  action: string
  state: string
  verdict: string | null
  manualHold: boolean
  reason: string | null
  eligibleAfter: string | null
  conceptNames: string[]
}
type Overview = {
  settings: { enabled: boolean; dailyBudget: string }
  policies: { action: string; coolingHours: number; maxDaily: number; maxArtworks: number }[]
  counts: { state: string; n: number }[]
  items: Item[]
  spending: { spent: number }
}
const base = '/api/knowledge-graph/grooming'
export function GroomingPanel({ compact = false }: { compact?: boolean }) {
  const client = useQueryClient()
  const [message, setMessage] = useState('')
  const query = useQuery<Overview>({
    queryKey: ['knowledge', 'grooming'],
    queryFn: async () => (await apiClient.get(base)).data,
    refetchInterval: 30000,
  })
  const change = useMutation({
    mutationFn: async ({ id, held }: { id: string; held: boolean }) =>
      apiClient.post(`${base}/${id}/hold`, { held }),
    onSuccess: () => {
      setMessage('Updated. Resuming requires fresh verification and a new cooling period.')
      client.invalidateQueries({ queryKey: ['knowledge', 'grooming'] })
    },
    onError: () => setMessage('Could not update this hold. Refresh and try again.'),
  })
  const configure = useMutation({
    mutationFn: async (enabled: boolean) => apiClient.patch(base, { enabled }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['knowledge', 'grooming'] }),
    onError: () => setMessage('Could not change grooming mode.'),
  })
  const restore = useMutation({
    mutationFn: async (id: string) => apiClient.post(`${base}/${id}/rollback`),
    onSuccess: () => {
      setMessage(
        'Restoration recorded. Structural restoration reaches the next build; relationship serving updates at publication.'
      )
      client.invalidateQueries({ queryKey: ['knowledge', 'grooming'] })
    },
    onError: () =>
      setMessage(
        'Restoration was held because the scope changed. Refresh and inspect the current graph.'
      ),
  })
  if (query.isLoading)
    return <p className="text-sm text-muted-foreground">Loading verified grooming…</p>
  if (!query.data || query.isError)
    return (
      <p role="alert" className="text-sm text-amber-800">
        Verified grooming status could not load.{' '}
        <button className="underline" onClick={() => query.refetch()}>
          Retry
        </button>
      </p>
    )
  const d = query.data
  const count = (state: string) => d.counts.find((c) => c.state === state)?.n ?? 0
  return (
    <section
      aria-label="Verified self-grooming"
      className="space-y-4 rounded-xl border border-arthaus-gold/25 bg-white p-5"
    >
      <div>
        <p className="text-[10px] uppercase tracking-widest text-arthaus-gold">
          Verified self-grooming · {d.settings.enabled ? 'Enabled' : 'Paused'}
        </p>
        <h2 className="mt-1 font-serif text-2xl">Review, mature, then apply.</h2>
        <p className="mt-2 text-sm text-slate-500">
          A separate model call checks each hypothesis against its current concept roster and
          calibration controls. Qualified changes mature for at least 24 hours. An untouched
          proposal is never approved by age alone.
        </p>
      </div>
      {!compact && (
        <Button
          size="sm"
          variant="outline"
          disabled={configure.isPending}
          onClick={() => configure.mutate(!d.settings.enabled)}
        >
          {d.settings.enabled ? 'Pause verified grooming' : 'Enable verified grooming'}
        </Button>
      )}
      <div className="flex flex-wrap gap-5 text-sm">
        <span>
          <strong>{count('scheduled')}</strong> scheduled
        </span>
        <span>
          <strong>{count('held')}</strong> held or researching
        </span>
        <span>
          <strong>{count('accepted')}</strong> accepted
        </span>
        <span>
          <strong>{count('expired')}</strong> expired
        </span>
        <span>
          Verification today: ${Number(d.spending.spent).toFixed(3)} / $
          {Number(d.settings.dailyBudget).toFixed(2)}
        </span>
      </div>
      {compact ? (
        <Link to="/app/knowledge/agent" className="text-sm underline">
          Inspect verification, schedules and holds
        </Link>
      ) : (
        <>
          <p className="text-xs text-slate-500">
            Merges, splits and relationship removals/replacements require two agreeing review calls
            and tighter impact limits. Uncertain cases retry daily, then expire after three
            attempts. Restoring a structural edit takes another graph build. Automated agreement is
            not independently measured semantic accuracy.
          </p>
          <div className="flex flex-wrap gap-3 text-xs text-slate-500">
            {d.policies.map((p) => (
              <span key={p.action}>
                {p.action}: {p.coolingHours}h cooling · up to {p.maxDaily} decisions/day · at most{' '}
                {p.maxArtworks.toLocaleString()} artworks (5,000 for structural changes/removals)
              </span>
            ))}
          </div>
          <p role="status" className="text-sm">
            {message}
          </p>
          <div className="max-h-[32rem] space-y-3 overflow-y-auto">
            {d.items.map((v) => (
              <article key={v.proposalId} className="rounded-lg border p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Link
                    to={`/app/knowledge/proposals?action=${v.action}`}
                    className="font-medium hover:underline"
                  >
                    {v.conceptNames.join(' ↔ ')}
                  </Link>
                  <span className="text-xs text-amber-800">
                    {v.state === 'scheduled'
                      ? `Scheduled ${v.verdict === 'reject' ? 'rejection' : 'acceptance'}`
                      : v.state.replace(/_/g, ' ')}
                  </span>
                </div>
                {v.state === 'scheduled' && v.eligibleAfter && (
                  <p className="mt-1 text-xs">
                    Eligible after{' '}
                    {new Date(v.eligibleAfter).toLocaleString('en-US', {
                      timeZone: 'America/Chicago',
                    })}{' '}
                    Chicago time, subject to fresh checks.
                  </p>
                )}
                <p className="mt-1 text-xs text-slate-500">{v.reason}</p>
                {v.state === 'accepted' && (
                  <p className="mt-1 text-xs text-slate-500">
                    {v.action === 'link'
                      ? 'Accepted in AMS; serving publication follows.'
                      : ['applied', 'already'].includes(v.appliedStatus || '')
                        ? 'Confirmed applied by a graph build.'
                        : 'Accepted; awaiting the next graph build.'}
                  </p>
                )}
                {['pending', 'held', 'verifying', 'scheduled'].includes(v.state) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    disabled={change.isPending}
                    onClick={() => change.mutate({ id: v.proposalId, held: !v.manualHold })}
                  >
                    {v.manualHold ? 'Resume fresh review' : 'Hold automatic decision'}
                  </Button>
                )}
                {['accepted', 'rejected'].includes(v.state) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    disabled={restore.isPending}
                    onClick={() => restore.mutate(v.proposalId)}
                  >
                    Restore previous state
                  </Button>
                )}
              </article>
            ))}
            {!d.items.length && (
              <p className="text-sm text-slate-500">
                The next hourly proposal pull will start verified grooming.
              </p>
            )}
          </div>
        </>
      )}
    </section>
  )
}
